/**
 * @changelog
 *
 * 2026-09-09 (0.1.0) (jhoward)
 *   - Add src\helpers\registerPushNotifications.ts to opt a device in to, and out of, offer notifications.
 *
 * 2026-09-09 (0.2.0) (jhoward)
 *   - Add isPushConfigured() so the opt-in can tell whether this bundle was built with the Firebase browser config.
 *   - Add syncPushRegistration(), a silent re-assertion of an already-granted device token. OPC Admin prunes tokens
 *     Firebase reports as dead and never calls back, so re-asserting is how the two sides converge again.
 */

import type { FetchResponse } from '@framework/defs/framework';
import getFirebaseMessagingToken from '@framework/helpers/getFirebaseMessagingToken';
import peppermillCustomFetchProxy from '@/helpers/peppermillCustomFetchProxy';
import { store as storeSubscription, destroy as destroySubscription } from '@rss/routes/push-subscription';

export interface PushRegistrationResult {
  success: boolean;
  message: string;
}

/**
 * Whether this browser can do web push at all. Safari only gained it in 16.4, and on iOS only for a site the guest has
 * added to their home screen, so a good number of visitors will land here false through no fault of the setup.
 */
export function isPushSupported(): boolean {
  return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
}

export function getPermissionState(): NotificationPermission | 'unsupported' {
  return isPushSupported() ? Notification.permission : 'unsupported';
}

/**
 * Whether this bundle was built with the Firebase browser config. These are build-time values, so a deploy that has
 * not been rebuilt since they were added will have them empty and no token can be minted - the server has no way of
 * knowing that, which is why the opt-in has to check here as well as asking the server.
 */
export function isPushConfigured(): boolean {
  return [
    import.meta.env.VITE_FIREBASE_API_KEY,
    import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
    import.meta.env.VITE_FIREBASE_PROJECT_ID,
    import.meta.env.VITE_FIREBASE_MESSAGE_SENDER_ID,
    import.meta.env.VITE_FIREBASE_APP_ID,
    import.meta.env.VITE_FIREBASE_VAPID_KEY,
  ].every((value) => !!value);
}

//One attempt per page load. Without this every navigation would queue another.
let syncInFlight: Promise<void> | null = null;

/**
 * Re-asserts a device token that has already been granted.
 *
 * OPC Admin deletes tokens Firebase reports as unregistered, and by design it never calls back here, so without this
 * a token pruned in error would stay missing until the guest happened to toggle the setting. FCM's getToken() returns
 * the same value for the same browser install, so re-posting it is cheap and idempotent.
 *
 * It must never prompt: it bails unless permission was already granted, so a guest who has not opted in - or who
 * declined - is never asked again as a side effect of navigating.
 */
export async function syncPushRegistration(): Promise<void> {
  if (syncInFlight) {
    return syncInFlight;
  }

  if (!isPushSupported() || 'granted' !== Notification.permission || !isPushConfigured()) {
    return;
  }

  //Keyed on the host because a guest can subscribe from more than one property domain, and each origin has its own
  //service worker and its own token.
  const markerKey = 'opc.push.lastSync.' + window.location.host;

  syncInFlight = (async () => {
    try {
      const registration = await navigator.serviceWorker.register(getServiceWorkerUrl(), { scope: '/' });
      await navigator.serviceWorker.ready;

      const token = await getFirebaseMessagingToken(registration);
      if (!token) {
        return;
      }

      //Twelve hours: long enough that this is not a request on every visit, short enough that a token pruned in
      //error comes back the same day.
      const marker = readMarker(markerKey);
      if (marker && marker.token === token && Date.now() - marker.sentAt < 43200000) {
        return;
      }

      const response = await peppermillCustomFetchProxy<FetchResponse>(
        { token },
        {
          route: storeSubscription(),
          showLoadingSpinner: false,
        },
      );

      if (200 === response.status) {
        writeMarker(markerKey, token);
      }
    } catch {
      //Silent by design - the guest did not ask for this and must not see it fail.
    } finally {
      syncInFlight = null;
    }
  })();

  return syncInFlight;
}

function readMarker(key: string): { token: string; sentAt: number } | null {
  try {
    const raw = window.localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function writeMarker(key: string, token: string): void {
  try {
    window.localStorage.setItem(key, JSON.stringify({ token, sentAt: Date.now() }));
  } catch {
    //Private browsing, or storage disabled. The only cost is re-posting the token next load.
  }
}

/**
 * Forgets the marker so the next load re-asserts immediately. Called on logout, because a device shared between two
 * guests must not wait twelve hours before the token follows the new account.
 */
export function clearPushRegistrationMarker(): void {
  try {
    window.localStorage.removeItem('opc.push.lastSync.' + window.location.host);
  } catch {
    //Nothing to clear.
  }
}

/**
 * The service worker is a static file, so its Firebase config travels as query parameters on the registration URL.
 * Registering the same script with different parameters would create a second worker, so the string has to stay stable.
 */
function getServiceWorkerUrl(): string {
  const parameters = new URLSearchParams({
    apiKey: import.meta.env.VITE_FIREBASE_API_KEY || '',
    authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN || '',
    projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID || '',
    storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET || '',
    messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGE_SENDER_ID || '',
    appId: import.meta.env.VITE_FIREBASE_APP_ID || '',
  });

  return '/firebase-messaging-sw.js?' + parameters.toString();
}

export default async function registerPushNotifications(): Promise<PushRegistrationResult> {
  if (!isPushSupported()) {
    return {
      success: false,
      message: 'This browser cannot show notifications. On an iPhone, add this site to your Home Screen first.',
    };
  }

  //Must be called from a user gesture, or the browser rejects it outright.
  const permission = await Notification.requestPermission();
  if ('granted' !== permission) {
    return {
      success: false,
      message:
        'denied' === permission
          ? 'Notifications are blocked for this site. You can turn them back on in your browser settings.'
          : 'Notifications were not enabled.',
    };
  }

  try {
    const registration = await navigator.serviceWorker.register(getServiceWorkerUrl(), { scope: '/' });
    await navigator.serviceWorker.ready;

    const token = await getFirebaseMessagingToken(registration);
    if (!token) {
      return { success: false, message: 'Notifications are not available right now. Please try again later.' };
    }

    const response = await peppermillCustomFetchProxy<FetchResponse>({ token }, { route: storeSubscription() });

    return {
      success: 200 === response.status,
      message: response.message || 'You will now get notified about your offers.',
    };
  } catch (err) {
    return { success: false, message: (err as Error).message || 'Could not enable notifications.' };
  }
}

export async function unregisterPushNotifications(): Promise<PushRegistrationResult> {
  let token: string | null = null;

  //Best effort: if the token cannot be read back, fall through and clear every device for this account instead.
  try {
    if (isPushSupported()) {
      const registration = await navigator.serviceWorker.getRegistration('/');
      token = await getFirebaseMessagingToken(registration ?? null);
    }
  } catch {
    token = null;
  }

  const response = await peppermillCustomFetchProxy<FetchResponse>(token ? { token } : {}, {
    route: destroySubscription(),
  });

  return {
    success: 200 === response.status,
    message: response.message || 'You will no longer get notified about your offers.',
  };
}
